import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BrandLockup } from "@/components/Brand";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const { login, register, loginGoogle, user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [signIn, setSignIn] = useState({ email: "", password: "" });
  const [signUp, setSignUp] = useState({ email: "", password: "", username: "" });

  React.useEffect(() => { if (user) navigate("/today"); }, [user, navigate]);

  const doLogin = async () => {
    setBusy(true);
    try {
      await login(signIn.email, signIn.password);
      toast.success("Welcome back");
      navigate("/today");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  const doRegister = async () => {
    setBusy(true);
    try {
      await register(signUp.email, signUp.password, signUp.username);
      toast.success("Account created");
      navigate("/today");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign up failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0B0C0E]">
      <div className="hidden lg:flex relative hero-sheen items-center justify-center p-12">
        <div className="grain" />
        <div className="relative max-w-md">
          <h2 className="text-3xl font-display font-semibold leading-tight">Every word is a room. Every letter is a key.</h2>
          <p className="mt-4 text-[var(--bone-muted)]">Sign in to track XP, decode reports, doctrine streaks, archive unlocks and more.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <button onClick={() => navigate("/")} className="mb-8"><BrandLockup /></button>
          <Tabs defaultValue="signin">
            <TabsList className="grid grid-cols-2 w-full bg-white/[0.04]">
              <TabsTrigger value="signin" data-testid="tab-signin">Sign in</TabsTrigger>
              <TabsTrigger value="signup" data-testid="tab-signup">Create account</TabsTrigger>
            </TabsList>

            <TabsContent value="signin" className="space-y-4 mt-5">
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input type="email" value={signIn.email} onChange={(e) => setSignIn({ ...signIn, email: e.target.value })}
                  className="bg-white/[0.04] border-white/[0.1]" data-testid="auth-email-input" placeholder="you@example.com" />
              </div>
              <div className="space-y-1.5">
                <Label>Password</Label>
                <Input type="password" value={signIn.password} onChange={(e) => setSignIn({ ...signIn, password: e.target.value })}
                  className="bg-white/[0.04] border-white/[0.1]" data-testid="auth-password-input"
                  onKeyDown={(e) => e.key === "Enter" && doLogin()} placeholder="••••••••" />
              </div>
              <Button onClick={doLogin} disabled={busy} className="w-full bg-[var(--primary)] hover:opacity-90" data-testid="auth-submit-button">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
              </Button>
            </TabsContent>

            <TabsContent value="signup" className="space-y-4 mt-5">
              <div className="space-y-1.5">
                <Label>Username</Label>
                <Input value={signUp.username} onChange={(e) => setSignUp({ ...signUp, username: e.target.value })}
                  className="bg-white/[0.04] border-white/[0.1]" data-testid="auth-username-input" placeholder="Your name" />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input type="email" value={signUp.email} onChange={(e) => setSignUp({ ...signUp, email: e.target.value })}
                  className="bg-white/[0.04] border-white/[0.1]" data-testid="auth-signup-email-input" placeholder="you@example.com" />
              </div>
              <div className="space-y-1.5">
                <Label>Password</Label>
                <Input type="password" value={signUp.password} onChange={(e) => setSignUp({ ...signUp, password: e.target.value })}
                  className="bg-white/[0.04] border-white/[0.1]" data-testid="auth-signup-password-input" placeholder="Min 6 characters" />
              </div>
              <Button onClick={doRegister} disabled={busy} className="w-full bg-[var(--primary)] hover:opacity-90" data-testid="auth-register-button">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
              </Button>
            </TabsContent>
          </Tabs>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-white/[0.08]" />
            <span className="text-xs text-[var(--bone-faint)]">or</span>
            <div className="h-px flex-1 bg-white/[0.08]" />
          </div>
          <Button variant="outline" onClick={loginGoogle} className="w-full glass border-[rgba(185,192,204,0.25)]" data-testid="auth-google-button">
            Continue with Google
          </Button>
          <p className="mt-4 text-xs text-[var(--bone-faint)] text-center">Demo: test@litrametry.com / Test1234!</p>
        </div>
      </div>
    </div>
  );
}
